import { getAbsenceCalendarCellLabel } from '../absenceCellLabel.js';
import {
  getState,
  isDoctorAbsent,
  getSlotBlocksForDoctorDate,
  getDesiderataForDoctorDate,
  canManagePlannerAbsenceRow,
} from '../store.js';
import { buildPlannerAbsenceChipsByDate } from '../plannerAbsenceRow.js';
import { getOfficialSlotsForDates } from '../shiftGenerator.js';
import { computeBimesterMirror, getBimesterForDate, findWorkloadGaps, isExcludedFromWorkloadBalance } from '../workload.js';
import {
  SLOT_CATEGORY_LABELS,
  SLOT_ABSENCE_LABELS,
  TIME_SLOT_LABELS,
  UOC_LABELS,
  WEEKDAYS,
  WEEKDAY_SHORT,
  DESIDERATA_LABELS,
} from '../types.js';
import {
  addCalendarDays,
  formatLocalISODate,
  getMondayOfWeek,
  getNextCalendarMonthBounds,
  getIsoWeekGridSpanForCalendarMonth,
  enumerateCalendarDaysInclusive,
  getWeekStart,
  hoursPaceBarHTML,
} from '../utils.js';

const SLOT_ORDER = {
  mattino: 0,
  pomeriggio: 1,
  prolungato: 2,
  notte: 3,
  reperibilita_giorno: 4,
  reperibilita_notte: 5,
};

const SLOT_WINDOWS = {
  mattino: { start: 8 * 60, end: 14 * 60, overnight: false },
  pomeriggio: { start: 14 * 60, end: 20 * 60, overnight: false },
  prolungato: { start: 8 * 60, end: 15 * 60 + 30, overnight: false },
  notte: { start: 20 * 60, end: 8 * 60, overnight: true },
  reperibilita_notte: { start: 20 * 60, end: 8 * 60, overnight: true },
  reperibilita_giorno: { start: 8 * 60, end: 20 * 60, overnight: false },
};

const CATEGORY_BADGES = {
  terapia_intensiva: 'ti',
  sala_operatoria: 'so',
  nora: 'nora',
  medicina_dolore: 'md',
  cure_palliative: 'cp',
  formazione: 'formazione',
  direzione: 'dir',
};

const DUTY_SEQUENCE = ['mattino', 'pomeriggio', 'notte'];
const DUTY_LABELS = {
  mattino: 'Mattino',
  pomeriggio: 'Pomeriggio',
  notte: 'Notte',
};

const DUTY_ROW_ORDER = {
  TI: 0,
  SO: 1,
  AMB_ANEST: 2,
  NORA: 3,
  CP: 4,
  MEDDOL: 5,
  FORMAZIONE: 6,
  DIREZIONE: 7,
  ALTRO: 8,
  REPERIBILITA: 9,
};

let kioskRefreshTimer = null;
let kioskRefreshToken = 0;

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeHtmlAttr(value) {
  return escapeHtml(value).replace(/\n/g, ' ');
}

/** Allineato alla logica classi in AbsencesCalendar (populate cells). */
function kioskAbsenceCellMeta(doctorId, dateStr, state) {
  const absent = isDoctorAbsent(doctorId, dateStr);
  const slotBlocks = getSlotBlocksForDoctorDate(doctorId, dateStr);
  const hasLibera = slotBlocks.liberaProfessione.length > 0;
  const hasFormazione = slotBlocks.formazione.length > 0;
  const desiderata = getDesiderataForDoctorDate(doctorId, dateStr) || [];
  let cellKind = 'available';
  if (absent) cellKind = 'blocked';
  else if (hasFormazione) cellKind = 'formazione';
  else if (hasLibera) cellKind = 'libera-professione';
  else if (desiderata.length) cellKind = 'desiderata';
  const weekMon = getWeekStart(dateStr);
  const reqLocked = state.currentUser?.role === 'user' && Boolean(state.absenceRequestLockedWeeks?.[weekMon]);
  return { absent, hasLibera, hasFormazione, desiderata, cellKind, reqLocked, slotBlocks };
}

function kioskAbsenceCellTitle(meta) {
  if (meta.absent) return 'Assente';
  if (meta.hasFormazione) {
    return `Formazione: ${meta.slotBlocks.formazione.map((s) => SLOT_ABSENCE_LABELS[s] || s).join(', ')}`;
  }
  if (meta.hasLibera) {
    return `Libera professione: ${meta.slotBlocks.liberaProfessione.map((s) => SLOT_ABSENCE_LABELS[s] || s).join(', ')}`;
  }
  if (meta.desiderata.length) return `Desiderata: ${meta.desiderata.map((t) => DESIDERATA_LABELS[t] || t).join(', ')}`;
  return 'Nessuna indicazione';
}

function cellHasAbsenceLike(doctorId, dateStr) {
  if (isDoctorAbsent(doctorId, dateStr)) return true;
  const sb = getSlotBlocksForDoctorDate(doctorId, dateStr);
  return sb.liberaProfessione.length > 0 || sb.formazione.length > 0;
}

function monthCalendarWeeksHaveLockedRequests(state, monthStart, monthEnd) {
  if (state.currentUser?.role !== 'user') return false;
  let mon = getMondayOfWeek(monthStart);
  const lastMon = getMondayOfWeek(monthEnd);
  while (mon <= lastMon) {
    if (state.absenceRequestLockedWeeks?.[mon]) return true;
    mon = addCalendarDays(mon, 7);
  }
  return false;
}

function renderNextMonthAbsencesCardHtml(doctorId, state, now, { showAbsencesCta }) {
  const { monthStart, monthEnd } = getNextCalendarMonthBounds(now);
  const { gridStart, gridEnd } = getIsoWeekGridSpanForCalendarMonth(monthStart, monthEnd);
  const spanDays = enumerateCalendarDaysInclusive(gridStart, gridEnd);
  if (!spanDays.length) return '';

  const monthLabelRaw = new Date(`${monthStart}T12:00:00`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  const monthLabel = monthLabelRaw ? monthLabelRaw.charAt(0).toUpperCase() + monthLabelRaw.slice(1) : monthStart;

  const monthDaysOnly = enumerateCalendarDaysInclusive(monthStart, monthEnd);
  let daysWithLabelInMonth = 0;
  let daysAbsenceLikeInMonth = 0;
  let daysDesiderataOnlyInMonth = 0;
  for (const d of monthDaysOnly) {
    const label = getAbsenceCalendarCellLabel(doctorId, d, gridStart, gridEnd);
    const meta = kioskAbsenceCellMeta(doctorId, d, state);
    if (label && String(label).trim()) daysWithLabelInMonth += 1;
    if (cellHasAbsenceLike(doctorId, d)) daysAbsenceLikeInMonth += 1;
    else if (meta.desiderata.length && label && String(label).trim()) daysDesiderataOnlyInMonth += 1;
  }

  const lockedHint = monthCalendarWeeksHaveLockedRequests(state, monthStart, monthEnd)
    ? '<p class="kiosk-muted kiosk-abs-mini-hint">Alcune settimane di questo mese sono bloccate per nuove richieste assenze/desiderata.</p>'
    : '';

  const statsLine = `<p class="kiosk-abs-mini-stats kiosk-muted">Nel mese: <strong>${daysWithLabelInMonth}</strong> giorni con indicazione`
    + (daysAbsenceLikeInMonth ? ` · assenza/LP/formazione: <strong>${daysAbsenceLikeInMonth}</strong>` : '')
    + (daysDesiderataOnlyInMonth ? ` · solo desiderata: <strong>${daysDesiderataOnlyInMonth}</strong>` : '')
    + '</p>';

  const headerCells = WEEKDAYS.map((k) => `<div class="kiosk-abs-mini-head">${WEEKDAY_SHORT[k]}</div>`).join('');

  const rows = [];
  for (let i = 0; i < spanDays.length; i += 7) {
    const slice = spanDays.slice(i, i + 7);
    const cells = slice.map((dateStr) => {
      const inMonth = dateStr >= monthStart && dateStr <= monthEnd;
      const meta = kioskAbsenceCellMeta(doctorId, dateStr, state);
      const label = getAbsenceCalendarCellLabel(doctorId, dateStr, gridStart, gridEnd);
      const domDay = parseInt(dateStr.slice(8, 10), 10);
      let cls = `calendar-cell kiosk-abs-mini-cell ${meta.cellKind === 'available' ? 'available' : meta.cellKind}`;
      if (!inMonth) cls += ' kiosk-abs-mini-outside-month';
      if (meta.reqLocked) cls += ' absence-request-locked-week';
      const title = kioskAbsenceCellTitle(meta) + (meta.reqLocked ? ' — Settimana bloccata per le richieste' : '');
      const wd = new Date(`${dateStr}T12:00:00`).getDay();
      const isWeekend = wd === 0 || wd === 6;
      if (isWeekend) cls += ' kiosk-abs-mini-weekend';
      return `
        <div class="${cls}" data-date="${escapeHtml(dateStr)}" title="${escapeHtmlAttr(title)}">
          <span class="kiosk-abs-mini-day">${domDay}</span>
          <span class="kiosk-abs-mini-label">${escapeHtml(label || '')}</span>
        </div>`;
    }).join('');
    rows.push(`<div class="kiosk-abs-mini-row">${cells}</div>`);
  }

  const cta = showAbsencesCta
    ? `<div class="kiosk-abs-mini-actions"><button type="button" class="btn btn-secondary" data-action="kiosk-open-absences-next-month">Gestisci in Assenze</button></div>`
    : '';

  return `
      <article class="card kiosk-section-card kiosk-next-month-absences">
        <div class="card-title">Le tue assenze del mese prossimo</div>
        <div class="kiosk-abs-mini-subtitle">${escapeHtml(monthLabel)}</div>
        ${statsLine}
        ${lockedHint}
        <div class="kiosk-abs-mini-calendar" role="grid" aria-label="Anteprima assenze e desiderata del mese prossimo">
          <div class="kiosk-abs-mini-row kiosk-abs-mini-row-head">${headerCells}</div>
          ${rows.join('')}
        </div>
        <p class="kiosk-abs-mini-legend kiosk-muted">Stesse abbreviazioni della pagina Assenze (assenze: sigle categoria; desiderata: NO M/P/N, NO RG, NO RN).</p>
        ${cta}
      </article>`;
}

function getSlotOrder(slot) {
  return SLOT_ORDER[slot?.timeSlot] ?? 99;
}

function getAssignedDoctorId(state, slot) {
  const key = slot?.slotKey || slot?.id;
  if (!key) return null;
  return state.shiftAssignmentsByKey?.[key] || null;
}

function getSortedSlots(slots) {
  return [...(slots || [])].sort((a, b) => {
    const byDate = String(a.date || '').localeCompare(String(b.date || ''));
    if (byDate !== 0) return byDate;
    const bySlot = getSlotOrder(a) - getSlotOrder(b);
    if (bySlot !== 0) return bySlot;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function isSlotActiveNow(slot, now) {
  if (!slot?.date) return false;
  const currentDate = formatLocalISODate(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const window = SLOT_WINDOWS[slot.timeSlot];
  if (!window) return false;
  if (!window.overnight) {
    return slot.date === currentDate && nowMinutes >= window.start && nowMinutes < window.end;
  }
  if (slot.date === currentDate && nowMinutes >= window.start) return true;
  return addCalendarDays(slot.date, 1) === currentDate && nowMinutes < window.end;
}

function getSlotStartMinutes(slot) {
  return SLOT_WINDOWS[slot?.timeSlot]?.start ?? 24 * 60;
}

function shouldHideExpiredTodaySlot(slot, now) {
  if (!slot?.date) return false;
  const today = formatLocalISODate(now);
  if (slot.date !== today) return false;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const start = getSlotStartMinutes(slot);
  if (nowMinutes < start) return false;
  return !isSlotActiveNow(slot, now);
}

function isSlotUpcomingToday(slot, now) {
  if (!slot?.date) return false;
  const currentDate = formatLocalISODate(now);
  if (slot.date !== currentDate) return false;
  return getSlotStartMinutes(slot) > now.getHours() * 60 + now.getMinutes();
}

function formatWeekdayDateLabel(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const weekdayRaw = d.toLocaleDateString('it-IT', { weekday: 'long' });
  const weekday = weekdayRaw ? weekdayRaw.charAt(0).toUpperCase() + weekdayRaw.slice(1) : '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${weekday} ${dd}-${mm}-${yyyy}`.trim();
}

function getDutyWindowStart(nowMinutes) {
  if (nowMinutes < 8 * 60) return { index: 2, dateOffset: -1 };
  if (nowMinutes < 14 * 60) return { index: 0, dateOffset: 0 };
  if (nowMinutes < 20 * 60) return { index: 1, dateOffset: 0 };
  return { index: 2, dateOffset: 0 };
}

function buildDutyWindows(now) {
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const start = getDutyWindowStart(nowMinutes);
  const today = formatLocalISODate(now);
  const windows = [];
  for (let i = 0; i < 3; i += 1) {
    const seqPos = start.index + i;
    const type = DUTY_SEQUENCE[seqPos % DUTY_SEQUENCE.length];
    const dayCarry = Math.floor(seqPos / DUTY_SEQUENCE.length);
    const date = addCalendarDays(today, start.dateOffset + dayCarry);
    windows.push({
      id: `${type}-${date}`,
      type,
      date,
      label: `${DUTY_LABELS[type] || type} ${formatWeekdayDateLabel(date)}`,
      isCurrent: i === 0,
    });
  }
  return windows;
}

function slotMatchesDutyWindow(slot, window) {
  if (!slot || !window) return false;
  if (!slot.date || slot.date !== window.date) return false;
  if (window.type === 'mattino') {
    return slot.timeSlot === 'mattino' || slot.timeSlot === 'prolungato' || slot.timeSlot === 'reperibilita_giorno';
  }
  if (window.type === 'pomeriggio') {
    return slot.timeSlot === 'pomeriggio' || slot.timeSlot === 'prolungato' || slot.timeSlot === 'reperibilita_giorno';
  }
  return slot.timeSlot === 'notte' || slot.timeSlot === 'reperibilita_notte';
}

function isReperibilitaSlot(slot) {
  const ts = String(slot?.timeSlot || '').toLowerCase();
  const at = String(slot?.activityType || '').toLowerCase();
  const name = String(slot?.name || '').toLowerCase();
  return ts.startsWith('reperibilita') || at === 'reperibilita' || name.startsWith('rep ');
}

function dutyRowGroup(slot) {
  if (isReperibilitaSlot(slot)) return 'REPERIBILITA';
  const category = String(slot?.category || '').toLowerCase();
  const name = String(slot?.name || '').toLowerCase().trim();
  if (category === 'terapia_intensiva') return 'TI';
  if (category === 'sala_operatoria') {
    if (name.startsWith('amb anest')) return 'AMB_ANEST';
    return 'SO';
  }
  if (category === 'nora') return 'NORA';
  if (category === 'cure_palliative') return 'CP';
  if (category === 'medicina_dolore') return 'MEDDOL';
  if (category === 'formazione') return 'FORMAZIONE';
  if (category === 'direzione') return 'DIREZIONE';
  return 'ALTRO';
}

function dutyRowPriority(slot) {
  return DUTY_ROW_ORDER[dutyRowGroup(slot)] ?? DUTY_ROW_ORDER.ALTRO;
}

function slotContextLabel(slot) {
  const scope = UOC_LABELS[slot?.uocId] || SLOT_CATEGORY_LABELS[slot?.category] || '';
  return scope || 'Contesto non definito';
}

function doctorLabel(doctor) {
  if (!doctor) return 'Medico non trovato';
  const parts = [doctor.cognome, doctor.nome].filter(Boolean);
  const full = parts.join(' ').trim();
  return doctor.sigla ? `${doctor.sigla} - ${full || doctor.sigla}` : (full || 'Medico');
}

function getAssignedDecoratedSlots(state, dates) {
  /** Allineato a Piano turni / Carichi: calendario + slot DB + sintetici da assegnazioni (merge per chiave logica). */
  const slots = getOfficialSlotsForDates(dates);
  return getSortedSlots(
    slots
      .map((slot) => ({
        ...slot,
        assignedDoctorId: getAssignedDoctorId(state, slot),
      }))
      .filter((slot) => Boolean(slot.assignedDoctorId))
  );
}

function computeKioskData(state, now, doctorId) {
  const today = formatLocalISODate(now);
  const next7Dates = Array.from({ length: 7 }, (_, i) => addCalendarDays(today, i));
  const dutyWindows = buildDutyWindows(now);
  const dutyDates = [...new Set(dutyWindows.map((w) => w.date))];

  const next7Assigned = getAssignedDecoratedSlots(state, next7Dates);
  const dutyAssigned = getAssignedDecoratedSlots(state, dutyDates);

  const ownNext7 = next7Assigned.filter((slot) => String(slot.assignedDoctorId) === String(doctorId));
  const ownLive = dutyAssigned.filter((slot) => String(slot.assignedDoctorId) === String(doctorId));

  const ownCurrent = ownLive.filter((slot) => isSlotActiveNow(slot, now));
  const ownToday = ownNext7.filter((slot) => slot.date === today);
  const ownUpcomingToday = ownToday.filter((slot) => isSlotUpcomingToday(slot, now));

  const doctorsMap = new Map((state.doctors || []).map((d) => [String(d.id), d]));
  const dutyWindowsRows = dutyWindows.map((window) => {
    const byDoctor = new Map();
    let windowSlots = dutyAssigned.filter((slot) => slotMatchesDutyWindow(slot, window));
    if (window.isCurrent) windowSlots = windowSlots.filter((slot) => isSlotActiveNow(slot, now));
    windowSlots.forEach((slot) => {
      const did = String(slot.assignedDoctorId);
      if (!byDoctor.has(did)) byDoctor.set(did, []);
      byDoctor.get(did).push(slot);
    });
    const rows = [...byDoctor.entries()]
      .map(([did, slots]) => ({
        doctorId: did,
        doctor: doctorsMap.get(did) || null,
        slots: getSortedSlots(slots).sort((a, b) => {
          const byPriority = dutyRowPriority(a) - dutyRowPriority(b);
          if (byPriority !== 0) return byPriority;
          const byName = String(a?.name || '').localeCompare(String(b?.name || ''));
          if (byName !== 0) return byName;
          return String(a?.timeSlot || '').localeCompare(String(b?.timeSlot || ''));
        }),
      }))
      .sort((a, b) => {
        const aPriority = (a.slots || []).reduce((min, s) => Math.min(min, dutyRowPriority(s)), 999);
        const bPriority = (b.slots || []).reduce((min, s) => Math.min(min, dutyRowPriority(s)), 999);
        if (aPriority !== bPriority) return aPriority - bPriority;
        const aDoc = a.doctor || {};
        const bDoc = b.doctor || {};
        const aKey = `${String(aDoc.cognome || '').toLowerCase()}|${String(aDoc.nome || '').toLowerCase()}|${a.doctorId}`;
        const bKey = `${String(bDoc.cognome || '').toLowerCase()}|${String(bDoc.nome || '').toLowerCase()}|${b.doctorId}`;
        return aKey.localeCompare(bKey);
      });
    return { ...window, rows };
  });

  const ownByDay = new Map();
  ownNext7.forEach((slot) => {
    if (!ownByDay.has(slot.date)) ownByDay.set(slot.date, []);
    ownByDay.get(slot.date).push(slot);
  });
  const ownByDayRows = next7Dates.map((date) => ({
    date,
    slots: getSortedSlots(ownByDay.get(date) || []).filter((slot) => !shouldHideExpiredTodaySlot(slot, now)),
  }));

  return {
    today,
    ownCurrent,
    ownToday,
    ownUpcomingToday,
    ownByDayRows,
    dutyWindowsRows,
  };
}

/** Ore con una cifra decimale, come nella vista Carichi. */
function kioskFmtHours(n) {
  return (Math.round((Number(n) || 0) * 10) / 10).toFixed(1);
}

/**
 * Saldo del bimestre in corso per il medico (Circolare ISS n. 6 del 3/7/2026).
 * Sta in Home Medico perché è la pagina su cui il medico atterra: in Carichi il dato c'è già,
 * ma ci arriva solo chi cambia vista di sua iniziativa.
 */
export function renderBimesterCardHtml(doctorId, state, now, { showWorkloadCta }) {
  const me = (state.doctors || []).find((d) => String(d.id) === String(doctorId));
  if (!me) return '';
  // Il Direttore fa solo turni Direzione: un saldo sul monte orario non lo riguarda.
  if (isExcludedFromWorkloadBalance(me)) return '';
  const bimester = getBimesterForDate(formatLocalISODate(now));
  if (!bimester) return '';

  let mirror;
  try {
    mirror = computeBimesterMirror(bimester.year, bimester.index, [me]);
  } catch {
    return '';
  }
  const m = mirror?.byDoctor?.[doctorId];
  if (!m) return '';
  // Medico senza monte orario (debito 0): la card non ha nulla da dire.
  if (!(m.dovute > 0)) return '';

  const endLabel = new Date(`${mirror.range.end}T12:00:00`).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // Allineamento a oggi: il saldo del bimestre conta anche le settimane non ancora redatte, quindi
  // da solo dice poco. Qui il confronto si ferma a dove i turni sono stati prodotti.
  const plannedThrough = mirror.plannedThrough;
  const planLabel = plannedThrough
    ? new Date(`${plannedThrough}T12:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })
    : '';
  // La barra parla solo dello stato a oggi: dove sei rispetto a dove dovresti essere alla data dei
  // turni pubblicati. Il monte del bimestre resta scritto sotto, senza barra: fino all'ultima
  // settimana redatta sarebbe corta comunque, e direbbe il falso.
  const suPiano = Boolean(plannedThrough) && m.dovuteAlPiano > 0;
  // Pallino e asta alla stessa data: le ore coperte dopo (ferie gia' registrate) vanno nel tratto chiaro.
  const coperteFuture = Math.max(0, m.coperte - m.coperteAlPiano);
  const conFuture = coperteFuture > 0.05;
  const barLabel = suPiano
    ? `Sei a ${kioskFmtHours(m.coperteAlPiano)}h; a oggi dovresti essere a ${kioskFmtHours(m.dovuteAlPiano)}h (turni pubblicati fino al ${planLabel})`
      + (conFuture ? `; altre ${kioskFmtHours(coperteFuture)}h già coperte dopo il ${planLabel}` : '')
      + `; il bimestre chiude a ${kioskFmtHours(m.dovute)}h`
    : '';
  const barHtml = suPiano
    ? `${hoursPaceBarHTML({ coperteAOggi: m.coperteAlPiano, coperteFuture, dovuteAOggi: m.dovuteAlPiano, dovuteTotali: m.dovute, label: barLabel, large: true })}
       <p class="kiosk-muted" style="margin: 0.2rem 0 0; font-size: 0.85rem;">
         La barra è l'intero bimestre (${kioskFmtHours(m.dovute)}h): il pallino è dove sei (<strong>${kioskFmtHours(m.coperteAlPiano)}h</strong>), l'asta dove dovresti essere a oggi (<strong>${kioskFmtHours(m.dovuteAlPiano)}h</strong>, turni pubblicati fino al ${escapeHtml(planLabel)})${conFuture ? `; il tratto chiaro sono altre <strong>${kioskFmtHours(coperteFuture)}h</strong> già coperte dopo quella data, per esempio ferie già registrate` : ''}.
       </p>`
    : '';
  let paceLine = '';
  if (plannedThrough) {
    const scarto = m.scostamentoAlPiano;
    const paceText = scarto < -0.05
      ? `<strong>${kioskFmtHours(-scarto)}h indietro</strong> sui turni già pubblicati`
      : (scarto > 0.05
          ? `<strong>${kioskFmtHours(scarto)}h avanti</strong> sui turni già pubblicati`
          : '<strong>In linea</strong> con i turni già pubblicati');
    const paceClass = scarto < -0.05 ? 'neg' : (scarto > 0.05 ? 'pos' : '');
    paceLine = `<p class="kiosk-bimester-pace ${paceClass}" style="font-size: 1.15rem; margin: 0.35rem 0 0;">${paceText} <span class="kiosk-muted" style="font-size: 0.85rem; white-space: nowrap;">(fino al ${escapeHtml(planLabel)})</span></p>`;
  }

  let headline;
  let headlineClass = '';
  if (m.oreMancanti > 0.05) {
    headline = `<strong>${kioskFmtHours(m.oreMancanti)}h</strong> ancora da assegnare`;
    headlineClass = 'negative';
  } else if (m.oreEsubero > 0.05) {
    headline = `<strong>+${kioskFmtHours(m.oreEsubero)}h</strong> oltre il monte del bimestre`;
    headlineClass = 'over';
  } else {
    headline = '<strong>Bimestre in pari</strong>';
  }

  const riporto = Math.abs(m.saldoApertura) > 0.05
    ? ` · riporto iniziale ${m.saldoApertura > 0 ? '+' : ''}${kioskFmtHours(m.saldoApertura)}h`
    : '';
  const provvisorie = m.turniProvvisori > 0.05
    ? ` Di queste, ${kioskFmtHours(m.turniProvvisori)}h sono su settimane non ancora archiviate e possono cambiare.`
    : '';

  // Il medico sa se era in ferie: è il primo a poter accorgersi di un'assenza non registrata.
  let gapWeeks = 0;
  let partialWeeks = 0;
  try {
    const g = findWorkloadGaps(mirror.range.start, mirror.range.end, [me]);
    gapWeeks = g.byDoctor[doctorId]?.length ?? 0;
    partialWeeks = g.partialByDoctor?.[doctorId]?.length ?? 0;
  } catch {
    gapWeeks = 0;
    partialWeeks = 0;
  }
  const totWeeks = gapWeeks + partialWeeks;
  const gapNote = totWeeks > 0
    ? `<p class="${gapWeeks > 0 ? 'negative' : 'kiosk-muted'}" style="margin: 0.5rem 0 0; font-size: 0.85rem;">
         ${gapWeeks > 0 ? '⚠' : '?'} ${totWeeks === 1 ? 'Una settimana risulta' : `${totWeeks} settimane risultano`}
         ${gapWeeks > 0 ? 'senza turni né assenze registrate' : 'molto sotto il tuo monte orario, senza assenze registrate'}.
         Se eri in ferie o in permesso, segnalalo: finché non è registrato le tue ore dovute risultano più alte del reale.
       </p>`
    : '';

  const cta = showWorkloadCta
    ? '<div class="kiosk-abs-mini-actions"><button type="button" class="btn btn-secondary" data-action="kiosk-open-carichi">Vedi in Carichi</button></div>'
    : '';

  return `
      <article class="card kiosk-section-card kiosk-bimester">
        <div class="card-title">Il tuo bimestre</div>
        <div class="kiosk-abs-mini-subtitle">${escapeHtml(mirror.range.label)} — chiude il ${escapeHtml(endLabel)}</div>
        ${paceLine}
        ${barHtml}
        <p class="kiosk-bimester-headline ${headlineClass}" style="font-size: ${paceLine ? '1rem' : '1.15rem'}; margin: 0.6rem 0 0;">${headline}</p>
        <p class="kiosk-muted" style="margin: 0.25rem 0 0;">
          Coperte <strong>${kioskFmtHours(m.coperte)}h</strong> su <strong>${kioskFmtHours(m.dovute)}h</strong> dovute nel bimestre${escapeHtml(riporto)}
        </p>
        <p class="kiosk-muted" style="margin: 0.25rem 0 0; font-size: 0.85rem;">
          Turni assegnati ${kioskFmtHours(m.turni)}h + assenze accreditate ${kioskFmtHours(m.assenze)}h. Le reperibilità non fanno parte del monte orario.${escapeHtml(provvisorie)}
        </p>
        ${gapNote}
        ${cta}
      </article>`;
}

function renderSlotChips(slots) {
  if (!slots?.length) return '<div class="kiosk-muted">Nessuna attivita in questa fascia.</div>';
  return slots.map((slot) => {
    const badge = CATEGORY_BADGES[slot.category] || 'dir';
    return `
      <div class="kiosk-slot-row">
        <span class="badge badge-${badge}">${escapeHtml(slot?.name || SLOT_CATEGORY_LABELS[slot?.category] || 'Attivita')}</span>
        <span>${escapeHtml(TIME_SLOT_LABELS[slot?.timeSlot] || slot?.timeSlot || '')}</span>
        <span class="kiosk-muted">${escapeHtml(slotContextLabel(slot))}</span>
      </div>
    `;
  }).join('');
}

function startAutoRefresh(container, opts) {
  clearAutoRefresh();
  kioskRefreshTimer = window.setInterval(() => {
    if (!container.isConnected) {
      clearAutoRefresh();
      return;
    }
    renderDoctorKioskView(container, opts);
  }, 90000);
}

function clearAutoRefresh() {
  if (kioskRefreshTimer) {
    clearInterval(kioskRefreshTimer);
    kioskRefreshTimer = null;
  }
}

export function disposeDoctorKioskView() {
  clearAutoRefresh();
}

/**
 * Assenze di oggi in coda alla card «Chi e in turno oggi»: sono gli stessi chip della cella
 * di oggi nella riga «Assenze» del Piano Turni, in sola lettura. Come nella griglia, la libera
 * professione dei colleghi resta a chi pianifica: e un dato fuori servizio.
 */
export function renderTodayAbsencesHtml(now) {
  const today = formatLocalISODate(now);
  const doctors = [...(getState().doctors || [])].sort((a, b) =>
    String(a?.cognome || '').localeCompare(String(b?.cognome || ''), 'it', { sensitivity: 'base' }));
  const canManage = Boolean(canManagePlannerAbsenceRow());
  const chips = (doctors.length ? (buildPlannerAbsenceChipsByDate([today], doctors).get(today) || []) : [])
    .filter((chip) => canManage || chip.kind !== 'lp');
  const body = chips.length
    ? chips.map((chip) => {
        const apex = chip.apex ? `<sup>${escapeHtml(chip.apex)}</sup>` : '';
        return `<span class="grid-absent-chip grid-absent-chip-${escapeHtmlAttr(chip.kind)} is-readonly" title="${escapeHtmlAttr(chip.title)}">${escapeHtml(chip.sigla)}${apex}</span>`;
      }).join('')
    : '<span class="kiosk-muted">Nessuna assenza registrata per oggi.</span>';
  return `
        <div class="kiosk-today-absences">
          <div class="kiosk-today-absences-title">Assenze di oggi</div>
          <div class="kiosk-today-absences-chips">${body}</div>
        </div>`;
}

export function renderDoctorKioskView(container, { onNavigate, availableViews = [], forceToken = null } = {}) {
  const state = getState();
  const user = state.currentUser || {};
  const doctorId = user.doctorId || user.doctor_id || null;
  const now = new Date();
  const nowLabel = now.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const renderToken = forceToken != null ? forceToken : (++kioskRefreshToken);
  if (!doctorId) {
    container.innerHTML = `
      <section class="kiosk-grid">
        <article class="card kiosk-hero-card">
          <div class="card-title">Home Medico</div>
          <p class="kiosk-muted">L'account non ha un codice medico assegnato. Usa le sezioni standard abilitate per il tuo ruolo.</p>
        </article>
      </section>
    `;
    clearAutoRefresh();
    return;
  }

  const data = computeKioskData(state, now, doctorId);
  const doctorsMap = new Map((state.doctors || []).map((d) => [String(d.id), d]));
  const me = doctorsMap.get(String(doctorId));
  const ownNowTitle = data.ownCurrent.length
    ? 'Attivita in corso'
    : (data.ownUpcomingToday.length ? 'Prossima attivita di oggi' : 'Nessun turno attivo');

  const ownNowRows = data.ownCurrent.length
    ? data.ownCurrent
    : data.ownUpcomingToday.slice(0, 2);

  const showAbsencesCta = Array.isArray(availableViews) && availableViews.includes('assenze');
  const nextMonthAbsencesCardHtml = renderNextMonthAbsencesCardHtml(doctorId, state, now, { showAbsencesCta });
  const showWorkloadCta = Array.isArray(availableViews) && availableViews.includes('carichi');
  const bimesterCardHtml = renderBimesterCardHtml(doctorId, state, now, { showWorkloadCta });
  const todayAbsencesHtml = renderTodayAbsencesHtml(now);

  container.innerHTML = `
    <section class="kiosk-grid">
      <article class="card kiosk-hero-card">
        <div class="kiosk-hero-head">
          <div class="card-title">Home Medico</div>
          <div class="kiosk-muted">Aggiornato: ${escapeHtml(nowLabel)}</div>
        </div>
        <div class="kiosk-hero-body">
          <div>
            <div class="kiosk-identity">${escapeHtml(doctorLabel(me))}</div>
          </div>
          <div class="kiosk-now">
            <div class="kiosk-now-title">${escapeHtml(ownNowTitle)}</div>
            ${renderSlotChips(ownNowRows)}
          </div>
        </div>
      </article>

      <article class="card kiosk-section-card">
        <div class="card-title">Chi è in turno oggi</div>
        <div class="kiosk-on-duty-list">
          ${data.dutyWindowsRows.some((window) => window.rows.length) ? data.dutyWindowsRows.map((window) => `
            <div class="kiosk-on-duty-row">
              <div class="kiosk-on-duty-doctor">${window.isCurrent ? 'In corso: ' : ''}${escapeHtml(window.label)}</div>
              <div class="kiosk-on-duty-slots">
                ${window.rows.length
                  ? window.rows.map((row) => `
                      <div class="kiosk-on-duty-assignment">
                        <span class="kiosk-on-duty-name">${escapeHtml(doctorLabel(row.doctor))}</span>
                        <span class="kiosk-on-duty-activities">
                          ${row.slots.map((slot) => {
                            const badge = CATEGORY_BADGES[slot.category] || 'dir';
                            return `<span class="badge badge-${badge}">${escapeHtml(slot?.name || SLOT_CATEGORY_LABELS[slot?.category] || 'Attivita')}</span> <span class="kiosk-muted">${escapeHtml(TIME_SLOT_LABELS[slot?.timeSlot] || slot?.timeSlot || '')}</span>`;
                          }).join(' · ')}
                        </span>
                      </div>
                    `).join('')
                  : '<span class="kiosk-muted">Nessuna assegnazione</span>'}
              </div>
            </div>
          `).join('') : '<div class="kiosk-muted">Nessuna assegnazione nelle fasce correnti.</div>'}
        </div>
        ${todayAbsencesHtml}
      </article>

      <article class="card kiosk-section-card">
        <div class="card-title">I tuoi prossimi 7 giorni</div>
        <div class="kiosk-next7">
          ${data.ownByDayRows.map((row) => `
            <div class="kiosk-day-block">
              <div class="kiosk-day-label">${escapeHtml(formatWeekdayDateLabel(row.date))}</div>
              ${row.slots.length
                ? row.slots.map((slot) => {
                    const badge = CATEGORY_BADGES[slot.category] || 'dir';
                    return `<div class="kiosk-day-slot"><span class="badge badge-${badge}">${escapeHtml(slot?.name || SLOT_CATEGORY_LABELS[slot?.category] || 'Attivita')}</span> <span>${escapeHtml(TIME_SLOT_LABELS[slot?.timeSlot] || slot?.timeSlot || '')}</span></div>`;
                  }).join('')
                : '<div class="kiosk-muted">Nessuna assegnazione</div>'}
            </div>
          `).join('')}
        </div>
      </article>

      ${bimesterCardHtml}

      ${nextMonthAbsencesCardHtml}

    </section>
  `;

  if (showAbsencesCta) {
    container.querySelector('[data-action="kiosk-open-absences-next-month"]')?.addEventListener('click', () => {
      if (typeof onNavigate === 'function') onNavigate('assenze', { primeNextMonthAbsences: true });
    });
  }

  if (showWorkloadCta) {
    container.querySelector('[data-action="kiosk-open-carichi"]')?.addEventListener('click', () => {
      if (typeof onNavigate === 'function') onNavigate('carichi');
    });
  }

  startAutoRefresh(container, { onNavigate, availableViews, forceToken: renderToken });
}
